import { useState, useEffect } from "react";
import { supabase } from "../lib/SupabaseClient";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X } from "lucide-react";

type Message = { from: "user" | "bot"; text: string };

export default function ChatbotWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [visitorId, setVisitorId] = useState<string | null>(null);
    const [isTyping, setIsTyping] = useState(false);

    // ✅ Optional registration form state
    const [showRegister, setShowRegister] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [company, setCompany] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ✅ Allow Navbar button or hero CTA to open chat
    useEffect(() => {
        const handler = () => setOpen(true);
        window.addEventListener("open-chat", handler);
        return () => window.removeEventListener("open-chat", handler);
    }, []);

    // ✅ Create anonymous visitorId automatically (no friction)
    useEffect(() => {
        try {
            let id = localStorage.getItem("visitorId");
            if (!id) {
                // Use a UUID so you can track anonymous sessions safely
                id = crypto.randomUUID();
                localStorage.setItem("visitorId", id);
            }
            setVisitorId(id);
        } catch {
            // If localStorage blocked, still allow chat (just no persistent id)
            setVisitorId(null);
        }
    }, []);

    /* ---------------- Optional Registration ---------------- */
    const handleRegister = async () => {
        if (!email || !name) {
            setError("Please provide at least your name and email.");
            return;
        }

        setLoading(true);
        setError(null);

        const { data, error } = await supabase
            .from("visitors")
            .insert([{ name, email, company }])
            .select("id")
            .single();

        setLoading(false);

        if (error) {
            setError(error.message);
            return;
        }

        // ✅ If you prefer to keep the SAME anonymous visitorId, store profile separately.
        // But if you want the DB id as visitorId, swap it in:
        if (data?.id) {
            localStorage.setItem("visitorDbId", data.id);
        }

        setShowRegister(false);
    };

    /* ---------------- Chat Logic ---------------- */
    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage: Message = { from: "user", text: input };
        setMessages((prev) => [...prev, userMessage]);
        setInput("");
        setIsTyping(true);

        try {
            const res = await fetch(
                "https://secureapi-prod-dccvbqhwh6fkeyfh.centralus-01.azurewebsites.net/api/chat",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    // ✅ send visitorId so backend can log/sessionize even if anonymous
                    body: JSON.stringify({ message: input, visitorId }),
                }
            );

            const data = await res.json();

            const botMessage: Message = {
                from: data.from || "bot",
                text:
                    data.text || "⚠️ Sorry, I couldn’t connect to SecureID AI right now.",
            };

            setMessages((prev) => [...prev, botMessage]);
        } catch {
            setMessages((prev) => [
                ...prev,
                { from: "bot", text: "⚠️ Connection error. Please try again later." },
            ]);
        } finally {
            setIsTyping(false);
        }
    };

    /* ---------------- UI ---------------- */
    return (
        <div className="fixed bottom-5 right-5 z-[3000] drop-shadow-2xl">
            {/* 💬 Floating Chat Button */}
            <AnimatePresence>
                {!open && (
                    <motion.button
                        onClick={() => setOpen(true)}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full p-4 shadow-lg flex items-center justify-center transition"
                    >
                        <MessageSquare size={22} />
                    </motion.button>
                )}
            </AnimatePresence>

            {/* 🪟 Chat Window */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 30, scale: 0.95 }}
                        transition={{ duration: 0.25 }}
                        className="w-80 h-[520px] bg-white shadow-2xl rounded-2xl flex flex-col border border-gray-200 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="bg-indigo-600 text-white p-3 rounded-t-2xl flex justify-between items-center">
                            <span className="font-semibold text-sm">
                                SecureID AI Assistant
                            </span>
                            <button
                                onClick={() => setOpen(false)}
                                className="hover:opacity-80 transition"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Optional: small CTA bar (anonymous by default) */}
                        <div className="px-3 py-2 bg-indigo-50 border-b text-xs text-gray-700 flex items-center justify-between gap-2">
                            <span className="truncate">
                                You can chat anonymously. Want follow-up? Register (optional).
                            </span>
                            <button
                                onClick={() => setShowRegister((v) => !v)}
                                className="text-indigo-700 font-semibold hover:underline whitespace-nowrap"
                            >
                                {showRegister ? "Hide" : "Register"}
                            </button>
                        </div>

                        {/* Registration (optional / collapsible) */}
                        {showRegister && (
                            <div className="p-3 border-b bg-white">
                                {error && (
                                    <p className="text-red-600 text-sm mb-2">{error}</p>
                                )}

                                <label className="text-xs font-medium text-gray-600 mb-1 block">
                                    Name
                                </label>
                                <input
                                    className="border p-2 w-full mb-2 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />

                                <label className="text-xs font-medium text-gray-600 mb-1 block">
                                    Email
                                </label>
                                <input
                                    className="border p-2 w-full mb-2 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Your email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />

                                <label className="text-xs font-medium text-gray-600 mb-1 block">
                                    Company (optional)
                                </label>
                                <input
                                    className="border p-2 w-full mb-3 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Company"
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                />

                                <button
                                    onClick={handleRegister}
                                    disabled={loading}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded font-medium transition w-full"
                                >
                                    {loading ? "Saving..." : "Save my info"}
                                </button>
                            </div>
                        )}

                        {/* Chat Window */}
                        <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
                            {messages.length === 0 && (
                                <div className="text-center text-gray-500 text-sm mt-8">
                                    👋 Hi there! I’m SecureID AI. How can I help you today?
                                </div>
                            )}

                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    className={`flex ${m.from === "user" ? "justify-end" : "justify-start"
                                        }`}
                                >
                                    <span
                                        className={`inline-block px-3 py-2 rounded-2xl text-sm ${m.from === "user"
                                                ? "bg-indigo-100 text-gray-800"
                                                : "bg-white border text-gray-700"
                                            }`}
                                    >
                                        {m.text}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Typing Indicator */}
                        {isTyping && (
                            <div className="px-3 py-2 text-sm text-gray-600 bg-gray-50 flex items-center gap-2 border-t">
                                <span className="font-medium text-indigo-600">SecureID AI</span>
                                <div className="flex space-x-1">
                                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" />
                                </div>
                            </div>
                        )}

                        {/* Input Bar */}
                        <div className="p-3 border-t bg-white flex flex-col gap-1">
                            <div className="flex gap-2">
                                <input
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                    placeholder="Ask me something..."
                                    className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                                <button
                                    onClick={handleSend}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                                >
                                    Send
                                </button>
                            </div>
                            <p className="text-[11px] text-gray-500 ml-1 select-none">
                                Press <span className="font-semibold text-gray-600">Enter</span>{" "}
                                to send
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
